import React, { useEffect, useRef, useState } from 'react';
import { proFetch } from '../../utils/proApi';
import { WALL_COLORS, FLOOR_TONES, LIGHTING, AMBIANCES } from './StyleStudio';

// Le style que le client a laissé entendre pendant la visite. La dictée est
// déjà transcrite dans le dossier ; on demande à l'IA d'y relever les goûts
// et les refus, et de choisir parmi les options de l'écran. Le métreur
// applique en un geste — ou ignore.

// `auto` : la proposition se fait d'elle-même à l'arrivée sur l'écran (une fois).
export default function StyleSuggestion({ leadRef, disponible, onApply, auto = false }) {
  const [busy, setBusy] = useState(false);
  const [erreur, setErreur] = useState('');
  const [sugg, setSugg] = useState(null);
  const [applique, setApplique] = useState(false);

  const proposer = async () => {
    setBusy(true); setErreur(''); setApplique(false);
    try {
      const r = await proFetch(`/leads/${leadRef}/style/suggerer`, {
        method: 'POST',
        body: JSON.stringify({ options: {
          ambiances: AMBIANCES.map(x => x.label), wallColors: WALL_COLORS.map(x => x.label),
          floorTones: FLOOR_TONES.map(x => x.label), lightings: LIGHTING.map(x => x.label),
        } }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Proposition impossible');
      setSugg(d);
    } catch (e) { setErreur(e.message); }
    finally { setBusy(false); }
  };

  const lancee = useRef(false);
  useEffect(() => {
    if (!auto || !disponible || lancee.current) return;
    lancee.current = true;
    proposer();
  }, [auto, disponible]); // eslint-disable-line react-hooks/exhaustive-deps

  const appliquer = () => {
    if (!sugg) return;
    onApply({
      style: {
        ambiance: AMBIANCES.find(x => x.label === sugg.ambiance) || null,
        wallColor: WALL_COLORS.find(x => x.label === sugg.wallColor) || null,
        floorTone: FLOOR_TONES.find(x => x.label === sugg.floorTone) || null,
        lighting: LIGHTING.find(x => x.label === sugg.lighting) || null,
      },
      brief: sugg.brief || '',
    });
    setApplique(true);
  };

  const choix = sugg && [['Ambiance', sugg.ambiance], ['Murs', sugg.wallColor], ['Sol', sugg.floorTone], ['Luminaires', sugg.lighting]].filter(([, v]) => v);

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-900">D'après la conversation</h3>
          <p className="text-xs text-slate-400">Ce que le client a dit aimer, ou refuser, pendant la visite — relevé dans la dictée.</p>
        </div>
        <button onClick={proposer} disabled={busy || !disponible}
                title={disponible ? '' : 'Aucune dictée transcrite sur ce dossier'}
                className="px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-bold">
          {busy ? 'Lecture de la dictée…' : sugg ? '↻ Proposer à nouveau' : '✨ Proposer un style'}
        </button>
      </div>
      {!disponible && <p className="text-xs text-slate-400 mt-2">Sans dictée transcrite, choisissez le style ci-dessous.</p>}
      {erreur && <p className="text-xs text-red-600 mt-2">{erreur}</p>}

      {sugg && (
        <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50/60 p-3">
          {choix.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {choix.map(([k, v]) => (
                <span key={k} className="inline-flex items-baseline gap-1 rounded-full bg-white border border-orange-200 px-2.5 py-1 text-xs">
                  <span className="text-slate-400">{k}</span><span className="font-semibold text-slate-800">{v}</span>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-600 mb-2">Le client n'a pas exprimé de préférence de couleur ou de matériau dans la dictée.</p>
          )}
          {sugg.brief && <p className="text-sm text-slate-700 leading-relaxed">{sugg.brief}</p>}
          {sugg.indices?.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {sugg.indices.map((x, i) => <li key={i} className="text-xs text-slate-500">« {x} »</li>)}
            </ul>
          )}
          <div className="flex items-center gap-3 mt-3">
            <button onClick={appliquer} className="px-3 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold">
              {applique ? '✓ Appliqué' : 'Appliquer ce style'}
            </button>
            <span className="text-[11px] text-slate-400">confiance {sugg.confiance} · la palette ci-dessous reste modifiable</span>
          </div>
        </div>
      )}
    </div>
  );
}
